import React, { useEffect, useMemo, useState } from 'react';
import { reportsApi, ebosApi, downloadAuthed } from '../api';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import BarChart from '../components/BarChart';
import SavedFiltersToolbar from '../components/SavedFiltersToolbar';
import { Download, Loader2 } from 'lucide-react';

const RANGE_OPTIONS = [
  { key: 'last_month', label: 'Last month' },
  { key: '3', label: 'Last 3 months' },
  { key: '6', label: 'Last 6 months' },
  { key: '12', label: 'Last 12 months' },
];

export default function Reports() {
  const { theme, user } = useAuth();
  const isSuper = user?.role === 'SuperAdmin';
  const [ebo, setEbo] = useState('all');
  const [range, setRange] = useState('6');
  const [ebos, setEbos] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { ebosApi.list().then(setEbos).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    reportsApi.testerToSales(ebo, range)
      .then(setData)
      .finally(() => setLoading(false));
  }, [ebo, range]);

  // Saved filter templates for this page (scope='reports'). Ops can bookmark
  // useful analytic views (e.g. "Delhi Q1", "All-India last 12 months").
  const currentFilters = useMemo(() => ({ ebo, range }), [ebo, range]);
  const applyReportsTemplate = (f) => {
    if (f.ebo) setEbo(f.ebo);
    if (f.range) setRange(f.range);
  };

  if (!data) return <div className="p-10 text-center text-[#7a6f5c]"><Loader2 className="animate-spin inline" /></div>;

  const chartData = data.rows.slice(0, 7).map(x => ({
    label: x.sku,
    value: x.testers,
    value2: x.sales > 10 ? x.sales / 10 : x.sales,
  }));

  return (
    <div>
      <PageHeader
        title="Tester-to-sales report"
        subtitle="Pieces sold against every tester unit provided, by SKU"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {isSuper && (
              <button
                data-testid="reports-tester-coverage-btn"
                onClick={() => downloadAuthed(reportsApi.testerCoverageExportUrl(), `tester-coverage-${new Date().getFullYear()}.xlsx`)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]"
                title="Download the SA-only Tester Coverage audit — every SKU + Tester SKU + brand + qty requested YTD"
              >
                <Download size={13}/> Tester coverage (YTD)
              </button>
            )}
            <button
              data-testid="reports-download-btn"
              onClick={() => downloadAuthed(reportsApi.testerToSalesExportUrl(ebo, range), `tester-to-sales-${ebo}-${range}.xlsx`)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-white text-[13px] font-medium"
              style={{ background: theme.primary }}
              title={`Download tester-to-sales as Excel for ${ebo === 'all' ? 'all EBOs' : ebo} · last ${range} months`}
            >
              <Download size={13}/> Download tester-to-sales
            </button>
          </div>
        }
      />

      <div className="fm-card p-5 mb-6">
        <div className="flex flex-wrap gap-6 items-end">
          <div>
            <div className="text-[12px] text-[#7a6f5c] mb-1">EBO</div>
            <select value={ebo} onChange={(e) => setEbo(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] min-w-[280px] bg-white">
              <option value="all">All EBOs</option>
              {ebos.map(e => <option key={e.code} value={e.code}>{e.name}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[12px] text-[#7a6f5c] mb-1">Date range</div>
            <select value={range} onChange={(e) => setRange(e.target.value)} className="fm-select fm-focus border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] min-w-[200px] bg-white">
              {RANGE_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
          </div>
          <div className="ml-auto pb-1">
            <SavedFiltersToolbar
              scope="reports"
              filters={currentFilters}
              onApply={applyReportsTemplate}
              theme={theme}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="fm-card p-5">
          <div className="text-[13px] text-[#7a6f5c]">Testers issued</div>
          <div className="font-serif text-[36px] mt-1" style={{ color: '#B78140' }}>{data.testersIssued}</div>
        </div>
        <div className="fm-card p-5">
          <div className="text-[13px] text-[#7a6f5c]">Pieces sold</div>
          <div className="font-serif text-[36px] mt-1 text-[#232323]">{data.piecesSold}</div>
        </div>
        <div className="fm-card p-5">
          <div className="text-[13px] text-[#7a6f5c]">Avg. pcs sold per tester</div>
          <div className="font-serif text-[36px] mt-1" style={{ color: theme.primary }}>{data.avg}</div>
        </div>
      </div>

      <div className="fm-card p-5 mb-6">
        <div className="text-[15px] font-semibold mb-3">Top SKUs — testers issued vs. sales (sales shown ÷10 to fit scale)</div>
        {loading ? <div className="p-8 text-center"><Loader2 className="animate-spin inline" /></div> :
          <BarChart data={chartData} primary="#B78140" secondary="#4D7C5F" height={220} />}
      </div>

      <div className="fm-card overflow-hidden">
        <table className="w-full text-[14px]">
          <thead>
            <tr className="text-left text-[12px] text-[#7a6f5c] border-b border-[#E7E2D3]">
              <th className="px-5 py-3 font-medium">SKU</th>
              <th className="px-5 py-3 font-medium">Product</th>
              <th className="px-5 py-3 font-medium text-center">Testers issued</th>
              <th className="px-5 py-3 font-medium text-center">Pieces sold</th>
              <th className="px-5 py-3 font-medium text-right pr-6">Pcs sold per tester</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map(x => (
              <tr key={x.sku} className="fm-row border-t border-[#EFEADC]">
                <td className="px-5 py-3 font-mono text-[13px]">{x.sku}</td>
                <td className="px-5 py-3 font-medium">{x.product}</td>
                <td className="px-5 py-3 text-center">{x.testers}</td>
                <td className="px-5 py-3 text-center">{x.sales}</td>
                <td className="px-5 py-3 text-right pr-6">
                  {x.testers ? (x.sales / x.testers).toFixed(1) : <span className="text-[#B78140]">No sales logged</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11.5px] text-[#7a6f5c] mt-4">A low “pcs sold per tester” flags branches issuing testers faster than sales justify. Sales are matched to the range by month (Period column on the <span className="font-semibold text-[#232323]">Sales data</span> page must be in YYYY-MM format).</p>
    </div>
  );
}
